import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { sectionsApi } from '@/api/sections'
import { usersApi } from '@/api/users'
import { X, Plus, Trash2 } from 'lucide-react'
import type { SectionOut, SectionPatch } from '@/types'
import CharCounter from '@/components/common/CharCounter'
import MoneyInput from '@/components/common/MoneyInput'

interface Props {
  section: SectionOut
  projectId: string
  onClose: () => void
  /** 'progress' — режим проектировщика: можно менять только % готовности. */
  mode?: 'full' | 'progress'
  /** Стоимости видят и правят только директор/зам; ГИП — нет. */
  showCost?: boolean
  /** Вызывается после успешного сохранения, если сервер вернул мягкое предупреждение. */
  onSaved?: (warning: string) => void
}

const schema = z
  .object({
    assignee_id:     z.string().optional(),
    planned_start:   z.string().min(1, 'Укажите дату начала'),
    planned_end:     z.string().min(1, 'Укажите плановый срок'),
    actual_end:      z.string().optional(),
    manual_progress: z.coerce.number().int().min(0).max(100).optional().or(z.literal('')),
    note:            z.string().max(1000, 'Максимум 1000 символов').optional(),
    cost:            z.string().optional(),
    name:            z.string().max(300, 'Максимум 300 символов').optional(),
    description:     z.string().max(500, 'Максимум 500 символов').optional(),
  })
  .refine((d) => d.planned_end >= d.planned_start, {
    message: 'Плановый срок должен быть не раньше даты начала',
    path: ['planned_end'],
  })
  .refine(
    (d) => !d.actual_end || d.actual_end >= d.planned_start,
    { message: 'Фактическая выдача не может быть раньше начала', path: ['actual_end'] },
  )

type FormData = z.infer<typeof schema>

type StageRow = {
  name: string
  start_date: string
  end_date: string
  cost: string
}

export default function EditSectionModal({ section, projectId, onClose, mode = 'full', showCost = true, onSaved }: Props) {
  const qc           = useQueryClient()
  const overlayRef   = useRef<HTMLDivElement>(null)
  const progressOnly = mode === 'progress'

  // Инициализируем этапы из текущих данных раздела
  const [stages, setStages] = useState<StageRow[]>(
    section.stages.map((s) => ({
      name:       s.name,
      start_date: s.start_date ?? '',
      end_date:   s.end_date   ?? '',
      cost:       s.cost       ?? '',
    }))
  )

  const { data: users = [] } = useQuery({
    queryKey: ['users-assignable'],
    queryFn:  () => usersApi.assignable().then((r) => r.data),
  })
  const assignees = users.filter((u) => u.role !== 'director' && u.is_active)

  const updateMutation = useMutation({
    mutationFn: (data: SectionPatch) => sectionsApi.patch(section.id, data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['sections', projectId] })
      qc.invalidateQueries({ queryKey: ['audit', projectId] })
      qc.invalidateQueries({ queryKey: ['gantt'] })
      qc.invalidateQueries({ queryKey: ['dashboard-zones'] })
      onClose()
      if (res.data.stage_cost_warning) onSaved?.(res.data.stage_cost_warning)
    },
    onError: (err: any) => {
      // Логируем детали ошибки в консоль для отладки
      console.error('[EditSection] PATCH error:', err?.response?.status, err?.response?.data)
    },
  })

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      assignee_id:     section.assignee_id ?? '',
      planned_start:   section.planned_start,
      planned_end:     section.planned_end,
      actual_end:      section.actual_end ?? '',
      manual_progress: section.manual_progress ?? ('' as any),
      note:            section.note ?? '',
      cost:            section.cost ?? '',
      name:            section.name ?? '',
      description:     section.description ?? '',
    },
  })

  const sectionStart = watch('planned_start')
  const sectionEnd = watch('planned_end')

  function stageDateOutOfRange(stage: StageRow): boolean {
    if (stage.start_date && sectionStart && stage.start_date < sectionStart) return true
    if (stage.end_date && sectionEnd && stage.end_date > sectionEnd) return true
    return false
  }

  function handleOverlayClick(e: React.MouseEvent) {
    if (e.target === overlayRef.current) onClose()
  }
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [onClose])

  // ── Управление этапами ──────────────────────────────────────────────────────
  function addStage() {
    setStages((prev) => [
      ...prev,
      { name: `Этап ${prev.length + 1}`, start_date: '', end_date: '', cost: '' },
    ])
  }
  function removeStage(idx: number) {
    setStages((prev) => prev.filter((_, i) => i !== idx))
  }
  function updateStage(idx: number, field: keyof StageRow, value: string) {
    setStages((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)))
  }

  function onSubmit(data: FormData) {
    if (progressOnly) {
      // Проектировщик: только % готовности (даты и этапы назначает ГИП)
      updateMutation.mutate({
        manual_progress: data.manual_progress !== '' && data.manual_progress !== undefined
          ? Number(data.manual_progress) : null,
      })
      return
    }
    const patch: SectionPatch = {
      assignee_id:     data.assignee_id || null,
      planned_start:   data.planned_start,
      planned_end:     data.planned_end,
      actual_end:      data.actual_end || null,
      manual_progress: data.manual_progress !== '' && data.manual_progress !== undefined
        ? Number(data.manual_progress) : null,
      note:        data.note        || null,
      name:        data.name        || null,
      description: data.description || null,
      // ГИП (showCost=false) не передаёт cost-ключи — бэкенд сохраняет стоимости, введённые директором
      ...(showCost ? { cost: data.cost || null } : {}),
      // Всегда передаём stages — даже пустой список означает «удалить все этапы»
      stages: stages.map((s) => ({
        name:       s.name,
        start_date: s.start_date || null,
        end_date:   s.end_date   || null,
        ...(showCost ? { cost: s.cost || null } : {}),
      })),
    }
    updateMutation.mutate(patch)
  }

  const cls = {
    input:  'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500',
    label:  'block text-sm font-medium text-gray-700 mb-1',
    error:  'mt-1 text-xs text-rose-600',
  }

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
    >
      <div className="w-full max-w-xl mx-4 rounded-xl bg-white shadow-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        {/* Шапка */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900">{progressOnly ? 'Изменить % готовности' : 'Редактировать раздел'}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {section.code}{section.code_name ? ` - ${section.code_name}` : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-6 space-y-4">

          {!progressOnly && (
            <>
              {/* Наименование подраздела */}
              <div>
                <label className={cls.label}>Наименование подраздела</label>
                <input
                  type="text"
                  {...register('name')}
                  maxLength={300}
                  placeholder="Вводится вручную"
                  className={cls.input}
                />
                {errors.name && <p className={cls.error}>{errors.name.message}</p>}
                <CharCounter value={watch('name')} max={300} />
              </div>

              {/* Наименование (пояснение) */}
              <div>
                <label className={cls.label}>Наименование (пояснение)</label>
                <input
                  type="text"
                  {...register('description')}
                  maxLength={500}
                  placeholder="Дополнительное пояснение"
                  className={cls.input}
                />
                {errors.description && <p className={cls.error}>{errors.description.message}</p>}
                <CharCounter value={watch('description')} max={500} />
              </div>

              {/* Исполнитель */}
              <div>
                <label className={cls.label}>Исполнитель</label>
                <select {...register('assignee_id')} className={cls.input}>
                  <option value=""> - не назначен - </option>
                  {assignees.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Плановые даты — проектировщику недоступны (сроки назначает ГИП) */}
          {!progressOnly && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={cls.label}>Дата начала *</label>
                <input type="date" {...register('planned_start')} className={cls.input} />
                {errors.planned_start && <p className={cls.error}>{errors.planned_start.message}</p>}
                <p className="mt-1 text-xs text-gray-400">Дата начала работ по разделу</p>
              </div>
              <div>
                <label className={cls.label}>Плановый срок *</label>
                <input type="date" {...register('planned_end')} className={cls.input} />
                {errors.planned_end && <p className={cls.error}>{errors.planned_end.message}</p>}
                <p className="mt-1 text-xs text-gray-400">Не раньше даты начала</p>
              </div>
            </div>
          )}

          {progressOnly && (
            <div>
              <label className={cls.label}>Готовность, %</label>
              <input
                type="number"
                min={0} max={100}
                {...register('manual_progress')}
                placeholder="Авто"
                className={cls.input}
              />
              {errors.manual_progress && (
                <p className={cls.error}>{errors.manual_progress.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">0-100; пусто = авторасчёт</p>
            </div>
          )}

          {!progressOnly && (
            <>
              {/* Фактическая дата и готовность */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={cls.label}>Факт. выдача</label>
                  <input type="date" {...register('actual_end')} className={cls.input} />
                  {errors.actual_end && <p className={cls.error}>{errors.actual_end.message}</p>}
                  <p className="mt-1 text-xs text-gray-400">Оставьте пустым если не выдан</p>
                </div>
                <div>
                  <label className={cls.label}>Готовность, %</label>
                  <input
                    type="number"
                    min={0} max={100}
                    {...register('manual_progress')}
                    placeholder="Авто"
                    className={cls.input}
                  />
                  {errors.manual_progress && (
                    <p className={cls.error}>{errors.manual_progress.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">0-100; пусто = авторасчёт</p>
                </div>
              </div>

              {/* Стоимость раздела — только директор/зам */}
              {showCost && (
                <div>
                  <label className={cls.label}>Стоимость раздела, ₽</label>
                  <Controller
                    name="cost"
                    control={control}
                    render={({ field }) => (
                      <MoneyInput
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="например 1 500 000"
                        className={cls.input}
                      />
                    )}
                  />
                  <p className="mt-1 text-xs text-gray-400">Стоимость без НДС, руб.</p>
                </div>
              )}

              {/* Примечание */}
              <div>
                <label className={cls.label}>Примечание</label>
                <textarea
                  {...register('note')}
                  rows={2}
                  maxLength={1000}
                  placeholder="Комментарий к разделу..."
                  className={`${cls.input} resize-none`}
                />
                {errors.note && <p className={cls.error}>{errors.note.message}</p>}
                <CharCounter value={watch('note')} max={1000} />
              </div>
            </>
          )}

          {/* Этапы раздела — проектировщику недоступны */}
          {!progressOnly && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">Этапы</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            {stages.length === 0 ? (
              <p className="text-xs text-gray-400 mb-2">
                Этапы не добавлены - раздел без разбивки на этапы
              </p>
            ) : (
              <div className="space-y-2 mb-2">
                {stages.map((stage, idx) => (
                  <div key={idx} className={`rounded-lg border p-3 ${
                    stageDateOutOfRange(stage) ? 'border-rose-300 bg-rose-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-gray-500 w-14 shrink-0">
                        Этап {idx + 1}
                      </span>
                      <input
                        type="text"
                        value={stage.name}
                        onChange={(e) => updateStage(idx, 'name', e.target.value)}
                        placeholder={`Этап ${idx + 1}`}
                        className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                      <button
                        type="button"
                        onClick={() => removeStage(idx)}
                        className="rounded p-1 text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-colors"
                        title="Удалить этап"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className={`grid ${showCost ? 'grid-cols-3' : 'grid-cols-2'} gap-2`}>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Начало</label>
                        <input
                          type="date"
                          value={stage.start_date}
                          onChange={(e) => updateStage(idx, 'start_date', e.target.value)}
                          className="block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Окончание</label>
                        <input
                          type="date"
                          value={stage.end_date}
                          onChange={(e) => updateStage(idx, 'end_date', e.target.value)}
                          className="block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                        <p className="mt-0.5 text-xs text-gray-400">≥ начала</p>
                      </div>
                      {showCost && (
                        <div>
                          <label className="block text-xs text-gray-500 mb-0.5">Стоимость, ₽</label>
                          <MoneyInput
                            value={stage.cost}
                            onChange={(v) => updateStage(idx, 'cost', v)}
                            placeholder="0"
                            className="block w-full rounded border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                          />
                        </div>
                      )}
                    </div>
                    {stageDateOutOfRange(stage) && (
                      <p className="mt-1 text-xs text-rose-600">
                        Даты этапа должны быть в пределах дат раздела ({sectionStart || '…'} - {sectionEnd || '…'})
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addStage}
              className="flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 font-medium"
            >
              <Plus className="h-4 w-4" />
              Добавить этап
            </button>
          </div>
          )}

          {updateMutation.isError && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              {(() => {
                const err = updateMutation.error as any
                const detail = err?.response?.data?.detail
                if (typeof detail === 'string') return detail
                if (Array.isArray(detail)) return detail.map((d: any) => d.msg ?? JSON.stringify(d)).join('; ')
                return 'Ошибка при сохранении. Попробуйте ещё раз.'
              })()}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="submit"
              disabled={isSubmitting || updateMutation.isPending}
              className="flex-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
